import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Award, CheckCircle2, XCircle, Clock, Search, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const VERTICAL_COLORS: Record<string, string> = {
  Fundable360: "bg-green-100 text-green-800 border-green-200",
  Share360: "bg-teal-100 text-teal-800 border-teal-200",
  Legacy360: "bg-purple-100 text-purple-800 border-purple-200",
};

const STATUS_CONFIG = {
  passed: { label: "Passed", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
};

export function CertificationPipeline() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [verticalFilter, setVerticalFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: certs = [], isLoading, refetch } = trpc.certification.adminList.useQuery({
    status: statusFilter !== "all" ? statusFilter as any : undefined,
    vertical: verticalFilter !== "all" ? verticalFilter as any : undefined,
  }, { refetchInterval: 60000 });

  const filtered = certs.filter((c: any) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const fullName = `${c.repFirstName ?? ""} ${c.repLastName ?? ""}`.toLowerCase();
    return (
      c.repCode?.toLowerCase().includes(s) ||
      c.vertical?.toLowerCase().includes(s) ||
      fullName.includes(s)
    );
  });

  // Stats
  const total = certs.length;
  const passed = certs.filter((c: any) => c.passed).length;
  const failed = certs.filter((c: any) => c.status === "failed").length;
  const byVertical: Record<string, number> = {};
  certs.forEach((c: any) => {
    if (c.passed) byVertical[c.vertical] = (byVertical[c.vertical] ?? 0) + 1;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Associate Certification Pipeline
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Track quiz attempts and certifications across all verticals</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Attempts" value={total} color="text-gray-700" />
        <StatCard label="Certified" value={passed} color="text-green-600" />
        <StatCard label="Failed" value={failed} color="text-red-600" />
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">By Vertical</p>
          <div className="flex flex-col gap-0.5">
            {["Fundable360", "Share360", "Legacy360"].map(v => (
              <div key={v} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{v}</span>
                <span className="font-semibold text-gray-800">{byVertical[v] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by rep code or vertical..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="passed">Passed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={verticalFilter} onValueChange={setVerticalFilter}>
          <SelectTrigger className="w-40 h-9 text-sm">
            <SelectValue placeholder="Vertical" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Verticals</SelectItem>
            <SelectItem value="Fundable360">Fundable360</SelectItem>
            <SelectItem value="Share360">Share360</SelectItem>
            <SelectItem value="Legacy360">Legacy360</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading certifications...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <Award className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No certification records found</p>
          <p className="text-sm text-gray-400 mt-1">Agents will appear here once they attempt a quiz</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Rep Code</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Name</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Vertical</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Score</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Attempt #</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Pipeline Stage</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((cert: any) => {
                const statusConf = STATUS_CONFIG[cert.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.pending;
                const StatusIcon = statusConf.icon;
                const isExpanded = expandedId === cert.id;
                return (
                  <>
                    <tr key={cert.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono font-semibold text-gray-800">{cert.repCode}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {cert.repFirstName || cert.repLastName
                          ? `${cert.repFirstName ?? ""} ${cert.repLastName ?? ""}`.trim()
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${VERTICAL_COLORS[cert.vertical] ?? "bg-gray-100 text-gray-700"}`}>
                          {cert.vertical}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusConf.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConf.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${cert.passed ? "text-green-600" : "text-red-600"}`}>
                          {cert.score}/{cert.totalQuestions}
                        </span>
                        <span className="text-gray-400 text-xs ml-1">({Math.round((cert.score / cert.totalQuestions) * 100)}%)</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">#{cert.attemptNumber}</td>
                      <td className="px-4 py-3">
                        {cert.ghlStageName ? (
                          <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">{cert.ghlStageName}</span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {cert.completedAt ? new Date(cert.completedAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : cert.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${cert.id}-expanded`} className="bg-gray-50">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="text-xs text-gray-600 space-y-1">
                            <div><span className="font-semibold">Wibiz Contact ID:</span> {cert.ghlContactId ?? "—"}</div>
                            <div><span className="font-semibold">Wibiz Opportunity ID:</span> {cert.ghlOpportunityId ?? "—"}</div>
                            <div><span className="font-semibold">Certified At:</span> {cert.certifiedAt ? new Date(cert.certifiedAt).toLocaleString() : "—"}</div>
                            <div><span className="font-semibold">Created At:</span> {cert.createdAt ? new Date(cert.createdAt).toLocaleString() : "—"}</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
